import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { switchMap, map, catchError, finalize, take } from 'rxjs/operators';
import { from, of, zip } from 'rxjs';
import { OrderAttributesFacade } from './order-attributes.facade';
import { TOrderAttributesInfoActionType } from './order-attributes.models';
import * as OrderAttributesActions from './order-attributes.actions';
import { Store } from '@ngrx/store';
import { TPlani8State } from '../reducers';

@Injectable({ providedIn: 'root' })
export class OrderAttributesEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private orderAttributesFacade: OrderAttributesFacade,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private store: Store<TPlani8State>,
  ) {}

  getOrderAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderAttributesInfoActionType.GetOrderAttributes),
      switchMap((action: OrderAttributesActions.GetOrderAttributesInfo) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        const orderAttributesInfo$ = this.plani8DataService.fetchOrderAttributesData(action.payload);
        const orderAttributesTypesInfo$ = this.plani8DataService.getOrderAttributesTypes();

        return orderAttributesInfo$.pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new OrderAttributesActions.GetOrderAttributesInfoSuccess(res);
          }),
          catchError(() => of(new OrderAttributesActions.GetOrderAttributesInfoFailure())),
        );
      }),
    ),
  );

  getOrderAttributesTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderAttributesInfoActionType.GetOrderAttributesTypes),
      switchMap((action: OrderAttributesActions.GetOrderAttributesTypesInfo) =>
        this.plani8DataService.getOrderAttributesTypes().pipe(
          map((res) => new OrderAttributesActions.GetOrderAttributesTypesInfoSuccess(res['types'])),
          catchError(() => of(new OrderAttributesActions.GetOrderAttributesInfoFailure())),
        ),
      ),
    ),
  );

  addUpdateOrderAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderAttributesInfoActionType.AddUpdateOrderAttributes),
      switchMap((action: OrderAttributesActions.AddUpdateOrderAttributes) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        return this.plani8DataService.addUpdateOrderAttributes(action.updateOrderAttributesPayload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new OrderAttributesActions.GetOrderAttributesInfo(action.getOrderAttributesPayload)),
          catchError(() => of(new OrderAttributesActions.AddUpdateOrderAttributesFailure())),
        );
      }),
    ),
  );

  deleteOrderAttributes = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderAttributesInfoActionType.DeleteOrderAttributes),
      switchMap((action: OrderAttributesActions.DeleteOrderAttributes) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        return this.plani8DataService.deleteOrderAttributes(action.deleteOrderAttributesPayload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new OrderAttributesActions.GetOrderAttributesInfo(action.getOrderAttributesPayload)),
          catchError(() => of(new OrderAttributesActions.DeleteOrderAttributesFailure())),
        );
      }),
    ),
  );
}
